import { PRODUCTS_INDEX, SERVICES_INDEX } from '@constant/common';
import { ORDER_COMPLETED } from '@constant/order';
import { CAT_PAYMENT_INV } from '@constant/userSearchSource';
import { disableLoader, enableLoader, showError } from '@context/actions';
import { useSharedLinkId } from '@hook/useSharedLinkResource';
import { getBillingData } from '@storeData/common';
import { getOrderByOrderId } from '@storeData/order';
import { getUserByGuestId } from '@storeData/user';
import { APISERVICE } from '@util/apiService/RestClient';
import { navigateTo } from '@util/routerService';
import { getCurrencySymbol, getTofixValue, isActiveAtBinaryIndex, processAppliedTaxes } from '@util/utils';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type PaymentInvoiceModalProps = {
    order: any;
    handleClose: () => void;
};

function PaymentInvoiceModal({ order, handleClose }: PaymentInvoiceModalProps) {
    const dispatch = useDispatch();
    const [orderData, setOrderData] = useState(order)
    const orderId = useSharedLinkId();
    const { configData } = useSelector((state: any) => state.store.storeData);
    const store = useSelector((state: any) => state);
    const languageData: any = store.languageKeywords
    const currentStoreId = store?.store?.storeData?.storeId || configData?.storeId;
    const [storeMetaData, setStoreMetaData] = useState<any>({});
    const [userDetails, setUserDetails] = useState(null);
    const tipConfig = configData?.storeConfig?.tipConfig;
    const isTipActive = tipConfig?.tip;
    const isPercentageTip = tipConfig?.percentageTip;
    const tipValues = tipConfig?.tipValues;
    const [selectedTip, setSelectedTip] = useState<number | string>('');
    const [tipAmount, setTipAmount] = useState<any>(0);
    const [customTipVal, setCustomTipVal] = useState('');
    const [pricingBreakdown, setPricingBreakdown] = useState({ total: 0, subTotal: 0, appliedTaxes: [] });
    const isDisposableAvailable = configData?.storeConfig?.basicConfig?.disposable_items;
    const [showTipDrawer, setShowTipDrawer] = useState(true);

    useEffect(() => {
        if (orderId && order) {
            dispatch(enableLoader());
            getBillingData(configData.tenantId, currentStoreId).then((res: any) => {
                setStoreMetaData(res);
            }).catch((error) => {
                console.error("Billing data error:", error);
            });

            if ((order?.statuses[order?.statuses?.length - 1]?.state == ORDER_COMPLETED)) {
                dispatch(disableLoader());
                navigateTo(`invoice/${orderId}`)
                return;
            }

            order.settledTiming = new Date().toLocaleString();
            getPricingBreakdown(order);
            setOrderData(order);
            getUserByGuestId(order?.tenantId, order?.storeId, order.guestId, CAT_PAYMENT_INV).then((response) => {
                dispatch(disableLoader());
                setUserDetails(response)
            }).catch(function (error) {
                dispatch(disableLoader());
                console.error("Guest data error:", error);
            })
        }
    }, [orderId, order])

    const calculateTaxes = (appliedTaxes, taxebalePrice: any, type: any, taxCharges) => {
        const hasItemTax = Boolean(taxCharges?.length);
        const taxesToProcess = hasItemTax ? taxCharges : (configData.txchConfig || []);
        const filteredTax = taxesToProcess.filter(taxData =>
            taxData.active && (hasItemTax || (((isActiveAtBinaryIndex(taxData.applyOn, PRODUCTS_INDEX) && type == "product") || (isActiveAtBinaryIndex(taxData.applyOn, SERVICES_INDEX) && type == "service")) && !taxData.charge)));

        return processAppliedTaxes(filteredTax, taxebalePrice, appliedTaxes);
    }

    const getPricingBreakdown = (order) => {
        let total: any = 0;
        let subTotal: any = 0;
        let appliedTaxes: any[] = [];
        order.products.map((cartItem) => {
            let applicablePrice: any = (userDetails?.membership?.length == 1 && userDetails?.membership[0].typeId == 4) ? (cartItem.memberPrice) * cartItem.quantity : (cartItem.salePrice || cartItem.billingPrice) * cartItem.quantity;
            subTotal += parseFloat(applicablePrice);
            total += parseFloat(applicablePrice);
            appliedTaxes = calculateTaxes(appliedTaxes, applicablePrice, cartItem.type, cartItem.taxCharges)
        })
        appliedTaxes.map((taxData: any) => {
            if (!taxData.isInclusive) total = getTofixValue(Number(total + taxData.total));
        })
        const dispTotal = Boolean(order?.disposables?.length) ? order?.disposables?.reduce((a: any, b: any) => a + (Number(b.price) * Number(b.quantity)), 0) : 0;
        total = dispTotal + Number(total);
        setPricingBreakdown({ total, subTotal, appliedTaxes });
    }


    const onPaymentClick = () => {
        dispatch(enableLoader());
        getOrderByOrderId(orderId, currentStoreId, configData?.tenantId).then((order: any) => {
            if (order) {
                dispatch(disableLoader());
                if ((order?.statuses[order?.statuses?.length - 1]?.state == ORDER_COMPLETED)) {
                    dispatch(showError(`Payment has already been completed.`))
                    navigateTo(`invoice/${orderId}`)
                } else {
                    if (isTipActive && tipAmount) {
                        let orderCopy = order;
                        orderCopy.totalTipAmount = tipAmount;
                        orderCopy.tips = getTipsObject(tipAmount, order?.products)
                        dispatch(enableLoader());
                        APISERVICE.POST(`${process.env.NEXT_PUBLIC_PCS_TXN}/orders/rzr/reinitiatePayment`, orderCopy).then((res) => {
                            setTimeout(() => {
                                const { Razorpay }: any = window;
                                const razorOptions = res.data
                                delete razorOptions.payment_link_id;
                                const rzp1 = new Razorpay(razorOptions);
                                rzp1.open();
                                dispatch(disableLoader());
                            }, 1000)
                        }).catch((error) => {
                            console.error(error)
                            dispatch(disableLoader());
                            dispatch(showError(`${languageData['Service Not Responding'] ? languageData['Service Not Responding'] : 'Service Not Responding'}`))
                        })
                    } else {
                        dispatch(enableLoader());
                        APISERVICE.GET(`${process.env.NEXT_PUBLIC_PCS_TXN}/rzr/optionsData/${orderData.id}/${orderData.tenantId}/${orderData.storeId}`).then((res) => {
                            // window.open(res.data.qr_image_url, '_blank');
                            setTimeout(() => {
                                const { Razorpay }: any = window;
                                const rzp1 = new Razorpay(res.data);
                                rzp1.open();
                                dispatch(disableLoader());
                            }, 1000)
                        }).catch((error) => {
                            console.error(error)
                            dispatch(disableLoader());
                            dispatch(showError(`${languageData['Service Not Responding'] ? languageData['Service Not Responding'] : 'Service Not Responding'}`))
                        })
                    }

                }
                setOrderData(order);
            }
        }).catch((error) => {
            dispatch(disableLoader());
            console.error("Payment status refresh failed:", error);
            dispatch(showError(`${languageData['Service Not Responding'] ? languageData['Service Not Responding'] : 'Service Not Responding'}`))
        })
    }


    const getTipsObject = (tipAmount: any, services: any) => {
        if (!services || services.length === 0 || !tipAmount) {
            return [];
        }
        const totalTip = Number(tipAmount);
        const serviceList = services.filter((s: any) => s.staffId);
        const totalItems = serviceList.length;
        if (totalItems === 0) return [];
        const staffServiceCount = new Map<number, { name: string, count: number }>();

        serviceList.forEach((service: any) => {
            const staffId = service.staffId;
            const staffName = service.staff;

            if (staffServiceCount.has(staffId)) {
                staffServiceCount.get(staffId)!.count += 1;
            } else {
                staffServiceCount.set(staffId, {
                    name: staffName,
                    count: 1
                });
            }
        });

        const tipsList: any[] = [];
        const currentTimestamp = new Date();
        let distributedTotal = 0;
        const uniqueStaffIds = Array.from(staffServiceCount.keys());
        uniqueStaffIds.forEach((staffId, index) => {
            const staffData = staffServiceCount.get(staffId)!;
            const proportion = staffData.count / totalItems;
            let calculatedTip = totalTip * proportion;
            let finalTipAmount = Math.round(calculatedTip);
            if (index === uniqueStaffIds.length - 1) {
                finalTipAmount = totalTip - distributedTotal;
            }
            distributedTotal += finalTipAmount;
            const tipObj = {
                "id": null,
                "orderId": null,
                "guestId": null,
                "staffId": staffId,
                "staffName": staffData.name,
                "orderDay": null,
                "tenantId": null,
                "storeId": null,
                "tipAmount": finalTipAmount,
                "guestName": null,
                "remark": null,
                "paymode": null,
                "orderInvoiceNo": null,
                "guestNumber": null,
                "payments": [
                    {
                        "id": 0,
                        "createdOn": currentTimestamp,
                        "createdBy": 0,
                        "name": "Razorpay",
                        "payment": finalTipAmount,
                        "txnId": "",
                        "bankTxnId": "0",
                        "cardNo": "",
                        "remark": ""
                    }
                ]
            };
            tipsList.push(tipObj);
        });
        return tipsList;
    }

    const calculateTipValue = (val: number) => {
        if (isPercentageTip) {
            return getTofixValue((orderData?.total * val) / 100);
        }
        return val;
    };

    const handleTipClick = (val: number, index: number) => {
        if (selectedTip === index) {
            setSelectedTip('');
            setTipAmount(0);
        } else {
            setSelectedTip(index);
            setTipAmount(calculateTipValue(val));
            setCustomTipVal('');
        }
        setShowTipDrawer(false);
    };

    const handleOtherClick = () => {
        document.getElementById('tip-custom-input')?.focus();
        if (selectedTip === 'other') {
            setSelectedTip('');
            setTipAmount(0);
            setCustomTipVal('');
        } else {
            setSelectedTip('other');
            setTipAmount(0);
        }
    };

    const handleCustomChange = (e: any) => {
        const val = e.target.value;

        if (val !== '' && selectedTip !== 'other') {
            setSelectedTip('other');
        }
        else if (val === '' && selectedTip === 'other') {
            setSelectedTip('');
        }

        if (Number(val) >= orderData?.total) {
            return;
        }
        if (!isNaN(val)) {
            setCustomTipVal(val);
            setTipAmount(Number(val));
        }
    };

    const onSubmitTip = (type: string) => {
        if (type === "skip") {
            setSelectedTip('');
            setTipAmount(0);
            setShowTipDrawer(false);
        } else if (type === "continue") {
            if (!Boolean(tipAmount)) {
                dispatch(showError(`Please select/enter tip amount`));
                return;
            }
            setShowTipDrawer(false);
        }
    }

    return (

        <div className="invoice-wrapper membership-invoice-wrapper tip-payment-flow">
            <Script src="https://checkout.razorpay.com/v1/checkout.js" strategy="lazyOnload" />
            {/* <div className='page-heading'>Order Invoice</div> */}
            {orderData ? <div className="invoice-page-wrap">
                <div className='salon-details-wrap'>
                    <div className='logo-wrap'>
                        <img src={storeMetaData?.logoPath} alt="Respark" />
                    </div>
                    <div className='salon-details'>
                        <div className='name'>
                            {storeMetaData?.tenantName && storeMetaData.tenantName}
                            {storeMetaData?.tenantName && storeMetaData?.storeName ? ', ' : ''}
                            {storeMetaData?.storeName && storeMetaData.storeName}
                        </div>
                        {storeMetaData.companyName && <div className='company-name'>{storeMetaData.companyName}</div>}
                        <div className='address s-detail'>{storeMetaData?.address}
                        </div>
                        <div className='phone s-detail'>{storeMetaData?.phone}
                            {storeMetaData?.phone1 && storeMetaData?.phone && <>, </>}
                            {storeMetaData?.phone1 && <>{storeMetaData?.phone1}</>}
                        </div>
                        <div className='email s-detail'>{storeMetaData?.email}</div>
                        {storeMetaData?.gstNo && <div className='gstn s-detail'>{languageData["GSTN"] ? languageData["GSTN"] : "GSTN"}: {storeMetaData?.gstNo}</div>}
                        {storeMetaData?.crNo && <div className='gstn s-detail'>{languageData["CRNo"] ? languageData["CRNo"] : "CRNo"}: {storeMetaData?.crNo}</div>}
                        {storeMetaData?.vatNo && <div className='gstn s-detail'>{languageData["VAT"] ? languageData["VAT"] : "VAT"}: {storeMetaData?.vatNo}</div>}
                    </div>
                </div>
                <div className='user-details-wrap'>
                    <div className='user-details'>
                        <div className='subheading'>{languageData["Bill to"] ? languageData["Bill to"] : "Bill to"}:</div>
                        <div className='name'>{userDetails?.firstName}</div>
                        <div className='phone'>{userDetails?.mobileNo} {userDetails?.email && <>{userDetails?.email}</>}</div>
                        {userDetails?.gstN && <div className='name'>{languageData["GSTN"] ? languageData["GSTN"] : "GSTN"}: {userDetails?.gstN}</div>}
                        {(userDetails?.addressList && userDetails?.addressList.length != 0 && userDetails?.addressList[0].line) ?
                            <div className='user-address phone'>{userDetails?.addressList[0].line},&nbsp;
                                {userDetails?.addressList[0].area && <>{userDetails?.addressList[0].area}, </>}
                                {userDetails?.addressList[0].landmark && <>{userDetails?.addressList[0].landmark}, </>}
                                {userDetails?.addressList[0].city && <>{userDetails?.addressList[0].city}, </>}
                                {userDetails?.addressList[0].code && <>{userDetails?.addressList[0].code} </>}
                            </div> :
                            <></>}
                    </div>
                    <div className='date-wrap'>
                        <div className='subheading'>{languageData["Date"] ? languageData["Date"] : "Date"}: <div className='time'>{orderData?.createdOn?.substring(0, 10)}</div></div>
                        <div className='subheading'>{languageData["Time"] ? languageData["Time"] : "Time"}: <div className='time'>{`${new Date(orderData?.createdOn).getHours()}:${new Date(orderData?.createdOn).getMinutes()}`}</div></div>
                    </div>
                </div>

                <div className="bill-item-type-wrap">
                    <div className="heading cap-text">{languageData['Purchased Bill'] ? languageData['Purchased Bill'] : 'Purchased Bill'}</div>
                    <div className='invoice-details-wrap order-invoice-details'>
                        <div className='services-list-wrap'>
                            <div className='heading-wrap d-f-c'>
                                <div className='name'>{languageData['Name'] ? languageData['Name'] : 'Name'}</div>
                                <div className='expert'>{languageData["Price"] ? languageData["Price"] : "Price"}</div>
                                <div className='qty'>{languageData['Qty'] ? languageData['Qty'] : 'Qty'}</div>
                                <div className='amt'>{languageData["Amt"] ? languageData["Amt"] : "Amt"}.</div>
                            </div>
                            <div className='details-wrap'>
                                <div className='width-100' >
                                    {orderData && orderData?.products?.map((item: any, index: any) => {
                                        return (<div className='service-details d-f-c'>
                                            <div className='name'>{item.name}</div>
                                            <div className='expert'>{item.billingPrice}</div>
                                            <div className='qty'>{item.quantity}</div>
                                            <div className='amt'>{getTofixValue(item.billingPrice * item.quantity)}</div>
                                        </div>)
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="invoice-total-details-wrap breakdown card">
                            <div className='pricing-details-wrap d-f-c'>
                                <div className='heading'>
                                    <div className='title'>{languageData["SubTotal"] ? languageData["SubTotal"] : "SubTotal"}</div>
                                    {(isDisposableAvailable && Boolean(orderData?.disposables?.length)) && <div className='title'>Extra Charges</div>}
                                    {pricingBreakdown.appliedTaxes?.map((taxData: any) => {
                                        return <div className='title' key={taxData.name}>{taxData.name}{taxData.isInclusive ? "(Inclusive)" : ''}({taxData.value}%)</div>
                                    })}
                                    <div className='title grand-total'>{languageData["Grand Total"] ? languageData["Grand Total"] : "Grand Total"}</div>
                                </div>
                                <div className='details'>
                                    <div className='value'>{getCurrencySymbol()} {pricingBreakdown.subTotal}</div>
                                    {(isDisposableAvailable && Boolean(orderData?.disposables?.length)) && <div className='value'>{getCurrencySymbol()} {orderData?.disposables?.reduce((a: any, b: any) => a + (Number(b.price) * Number(b.quantity)), 0)}</div>}
                                    {pricingBreakdown.appliedTaxes?.map((taxData: any, index: number) => {
                                        return <div className='value' key={taxData.name}>{getCurrencySymbol()} {taxData.total}</div>
                                    })}
                                    <div className='value grand-total'>{getCurrencySymbol()} {pricingBreakdown.total}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {isTipActive && (
                    <div className={`add-tip-btn-wrap ${showTipDrawer ? 'hide' : ''}`} onClick={() => setShowTipDrawer(true)}>
                        {tipAmount ?
                            <div className="add-tip-action">
                                <div className="label">Tip added {selectedTip !== "other" ? `(${tipValues[selectedTip]}%)` : ""} :</div>
                                <div className="value">{getCurrencySymbol()} {tipAmount}</div>
                            </div>
                            :
                            <div className="add-tip-action">
                                <div className="label">Tip Not added :</div>
                                <div className="value">Add Tip</div>
                            </div>
                        }
                    </div>
                )}

            </div> : <>
                {orderId ? <div className='no-data card'>
                    {languageData["The invoice you are looking for is not available"] ? languageData["The invoice you are looking for is not available"] : "The invoice you are looking for is not available"}
                </div> : <div className='no-data card'>
                    {languageData["Invalid link"] ? languageData["Invalid link"] : "Invalid link"}
                </div>}
            </>}
            {orderData && <>
                {isTipActive && showTipDrawer && (
                    <div className="tip-component-wrapper">
                        <div className="tip-heading">{languageData['Would you like to add tip?'] ? languageData['Would you like to add tip?'] : 'Would you like to add tip?'}</div>
                        <div className="tip-options-row">
                            {tipValues.map((val, index) => (
                                <div key={index} className="tip-option-wrap">
                                    {/* {isPercentageTip ? <div className="amount-text">{val}%</div> : null} */}
                                    <div className={`tip-option ${selectedTip === index ? 'active' : ''}`} onClick={() => handleTipClick(val, index)}>
                                        {/* <div className="amount-text">{getCurrencySymbol()}{getTofixValue((pricingBreakdown?.total * val) / 100)}</div> */}
                                        <div className="amount-text">{!isPercentageTip && getCurrencySymbol()}{val}{isPercentageTip && '%'}</div>
                                        {index === 2 && (<div className="most-tipped-badge">Most Tipped</div>)}
                                        {selectedTip === index && (<div className="bg-overlay"></div>)}
                                    </div>
                                </div>
                            ))}
                            <div className='tip-option-wrap'>
                                <div className={`tip-option ${selectedTip === 'other' ? 'active' : ''}`} onClick={handleOtherClick}>
                                    <span className="label">Other</span>
                                </div>
                            </div>
                        </div>

                        {selectedTip === 'other' && <div className="tip-input-section">
                            <span className="currency-prefix">{getCurrencySymbol()}</span>
                            <input
                                id='tip-custom-input'
                                type="text"
                                className="tip-custom-input"
                                placeholder="Enter Tip Amount"
                                value={selectedTip === 'other' ? customTipVal : (selectedTip !== '' ? tipAmount : '')}
                                onChange={handleCustomChange}
                            // disabled={selectedTip !== 'other' && selectedTip !== ''}
                            />
                            <div className={`action-btn ${(!customTipVal) ? 'disabled' : ''}`} onClick={() => onSubmitTip("continue")}>Done</div>
                        </div>}

                        <div className='action-btn'>
                            <div className="skip-btn" onClick={() => onSubmitTip("skip")}>Skip Tip</div>
                        </div>
                    </div>
                )}
                <div className={`pay-btn-wrap ${showTipDrawer ? 'hide' : ''}`}>
                    <div className="summary-text">
                        <span className="label">Total Amount To Pay</span>
                        <span className="value">{getCurrencySymbol()}{Number(pricingBreakdown.total) + Number(tipAmount)}</span>
                    </div>
                    <button className="primary-btn rounded-btn long-btn" onClick={onPaymentClick}>{languageData["PAY NOW"] ? languageData["PAY NOW"] : "PAY NOW"}</button>
                </div>
            </>}
        </div>
    )
}

export default PaymentInvoiceModal
